import { SlopeMeasurementSettings, SlopeAngleFormat } from '@trimble/types';
import { t } from '@trimble/i18n';
import * as Converter from '../unit-of-measure/slope-converter';
import { DEFAULT_LOCALE, DEFAULT_LOCALE_OPTIONS } from './formatting-defaults';
import { BaseFormatterProps } from './base-formatter-props.types';

const maxPrecision = 2;

/**
 * The Fulcrum library doesn't support slope measurements and so this utility doesn't have a common base unit to convert through.
 * Measurements are converted directly between the specified units.
 *
 * @regex ^-?(1:\d+(\.\d+)?|\d+(\.\d+)? °|\d+(\.\d+)? %)$
 * - Note: Output is not explicitly capped at 100% or 360°
 * but mapping from other units to degrees will return values from [-90, 90] degrees.
 */
export const toDisplay = ({
  measuredValue,
  currentUnits,
  settings,
}: BaseFormatterProps<
  number,
  SlopeAngleFormat,
  SlopeMeasurementSettings
>): string => {
  switch (settings.format) {
    case 'degrees':
      /** @regex ^-?\d+(\.\d+)? °$ */
      return numberToDegrees(measuredValue, currentUnits);
    case 'percentage':
      /** @regex ^-?\d+(\.\d+)? %$ */
      return numberToPercentage(measuredValue, currentUnits);
    case 'ratio':
      /** @regex ^-?1:\d+$ */
      return numberToRatio(measuredValue, currentUnits);
    default:
      return `${measuredValue.toFixed()} ${currentUnits}`; // Fallback to raw value if format is unrecognized
  }
};

const numberToDegrees = (
  value: number,
  currentUnits: SlopeAngleFormat
): string => {
  const degreeValue = Converter.mapToDegrees({
    value: value,
    baseUnit: currentUnits,
  });
  if (degreeValue !== null) {
    return `${degreeValue.toLocaleString(DEFAULT_LOCALE, DEFAULT_LOCALE_OPTIONS(maxPrecision))} °`;
  } else {
    return t('Utilities.Formatting.InvalidUnit', {
      defaultValue: 'Invalid Unit',
    });
  }
};

const numberToPercentage = (
  value: number,
  currentUnits: SlopeAngleFormat
): string => {
  const percentValue = Converter.mapToPercent({
    value: value,
    baseUnit: currentUnits,
  });
  if (percentValue !== null) {
    return `${percentValue.toLocaleString(DEFAULT_LOCALE, DEFAULT_LOCALE_OPTIONS(maxPrecision))} %`;
  } else {
    return t('Utilities.Formatting.InvalidUnit', {
      defaultValue: 'Invalid Unit',
    });
  }
};

const numberToRatio = (
  value: number,
  currentUnits: SlopeAngleFormat
): string => {
  const ratioValue = Converter.mapToRatio({
    value: value,
    baseUnit: currentUnits,
  });
  switch (true) {
    case ratioValue == 0:
      return '0:1';
    case ratioValue == Infinity:
      return '1:0';
    case ratioValue == -Infinity:
      return '-1:0';
    case ratioValue == null:
      return t('Utilities.Formatting.InvalidUnit', {
        defaultValue: 'Invalid Unit',
      });
    case (ratioValue ?? 0) < 0:
      return `-1:${(1 / Math.abs(ratioValue)).toLocaleString(DEFAULT_LOCALE, DEFAULT_LOCALE_OPTIONS(maxPrecision))}`;
    case (ratioValue ?? 0) > 0:
      return `1:${(1 / ratioValue).toLocaleString(DEFAULT_LOCALE, DEFAULT_LOCALE_OPTIONS(maxPrecision))}`;
    default:
      return t('Utilities.Formatting.Error', {
        defaultValue: 'Failed to format value',
      });
  }
};
